// Singleton-Store fuer den Local-API-Bypass (Auth-Umgehung fuer
// localhost/Docker). Speist den Warn-Banner (BypassBanner.svelte) + das
// Schloss-Symbol in der TopBar.
//
// Datenfluss (seit 03.11.2026 über das Homelab-Protokoll, docs/protokoll.md
// › „Erreichbarkeit, DNS und Bypass über das Protokoll“): die Rohfelder der
// Einstellung (an, Auto-Aus in Minuten, seit wann) sind das Topic bypass.lage.
// Das Backend (internal/protokoll/zugang) setzt es auf Anstoß (Schalter,
// Auto-Aus-Einstellung, Auto-Aus der Anmeldung) und alle 60 s. Bis dahin kamen
// die Rohfelder aus GET /v2-api/settings/local-api-access (Boot-Poll, 30 s) und
// dem WS-Kanal "security:bypass" (abgebaut 03.11.2026).
//   - REST (`GET /v2-api/settings/local-api-access`) nur beim Start, als
//     Revalidator (Reconnect, Tab kommt zurück), nach einem fehlgeschlagenen
//     Ausschalten und als Neuversuch alle 15 s, solange ein Abruf scheiterte.
//   - Ein Stand, der während eines Abrufs kommt, liegt über dessen Antwort; ein
//     älterer Stand überschreibt eine frischere Antwort nicht.
//   - Der 1-s-Ticker bleibt: er zählt die Restzeit lokal aus enabled_at+
//     auto_off_min runter und blendet den Banner im richtigen Moment selbst aus
//     -- der Auto-Off im Backend ist lazy (erst beim nächsten Request), daher
//     darf der Banner nicht auf einen Server-Frame warten.
//
// Bewusst KEIN localStorage-SWR: ein gecachtes "Bypass aktiv" wuerde bei
// jedem Reload kurz aufblitzen, obwohl er laengst aus ist (transient-
// warning-Lektion, analog critical-summary).

import { SvelteMap } from "svelte/reactivity";
import { APIError, getJSON, postJSON } from "./api";
import { bypassAusProtokoll, bypassAusRest, type BypassRoh } from "./erreichbarkeit-protokoll";
import { t } from "./i18n.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { BypassLage } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";
import { toast } from "./toast.svelte";

export interface BypassState {
  active: boolean;
  enabled: boolean;
  autoOffMin: number;
  enabledAt: string;
  remainingS: number;
}

const _state = new SvelteMap<string, BypassState>();
// Die Rohfelder; `undefined`, solange weder Abruf noch Stand etwas geliefert haben.
let _raw: BypassRoh | undefined;
// Laufende Nummer des jüngsten Stands (siehe dns-health.svelte.ts).
let _nr = 0;
let _unsub: Array<() => void> = [];
let _tick: ReturnType<typeof setInterval> | undefined;
let _started = false;

// recompute leitet active + remainingS aus den rohen Feldern + jetzt ab --
// dieselbe Logik wie settings.BypassActive im Backend. Gesetzt wird nur bei
// einer Änderung (der Ticker käme sonst jede Sekunde als Update an alle Leser).
function recompute(): void {
  if (!_raw) return;
  const { enabled, autoOffMin, enabledAt } = _raw;
  let active = enabled;
  let remainingS = 0;
  if (enabled && autoOffMin > 0 && enabledAt) {
    const deadline = Date.parse(enabledAt) + autoOffMin * 60_000;
    const ms = deadline - Date.now();
    if (Number.isNaN(deadline)) {
      active = true; // unparsebarer Stempel -> wie "gerade an"
    } else if (ms <= 0) {
      active = false;
    } else {
      active = true;
      remainingS = Math.floor(ms / 1000);
    }
  }
  const alt = _state.get("current");
  if (
    alt &&
    alt.active === active &&
    alt.enabled === enabled &&
    alt.autoOffMin === autoOffMin &&
    alt.enabledAt === enabledAt &&
    alt.remainingS === remainingS
  ) {
    return;
  }
  _state.set("current", { active, enabled, autoOffMin, enabledAt, remainingS });
}

function setzeRoh(neu: BypassRoh): void {
  _raw = neu;
  recompute();
}

function beiLage(_instanz: string, daten: Record<string, unknown>): void {
  _nr++;
  setzeRoh(bypassAusProtokoll(skaliere<BypassLage>("bypass.lage", daten)));
}

// Nach einem gescheiterten Abruf ein neuer Versuch (es gibt keinen Takt mehr).
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;
// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach dem Stopp
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

async function refresh(): Promise<void> {
  const ab = _nr;
  const lauf = _lauf;
  let rest: BypassRoh | undefined;
  try {
    rest = bypassAusRest(await getJSON<Parameters<typeof bypassAusRest>[0]>("/v2-api/settings/local-api-access"));
  } catch {
    rest = undefined;
  }
  if (lauf !== _lauf) return;
  if (rest) {
    if (_nr <= ab) setzeRoh(rest);
  } else if (_started && !_neuversuch) {
    _neuversuch = setTimeout(() => {
      _neuversuch = undefined;
      if (_started) void refresh();
    }, NEUVERSUCH_MS);
  }
}

export function startBypassSummary(): void {
  if (_started) return;
  _started = true;
  _lauf++;
  void refresh();
  _unsub.push(abonniere("bypass.lage", { rate: "normal" }, { stand: beiLage }));
  // ⚠️ Der Protokoll-Kanal allein reicht NICHT: nach einem abgerissenen Kanal
  // (jeder Deploy) holt der Revalidator den Stand frisch.
  _unsub.push(registerRevalidator(refresh));
  _tick = setInterval(recompute, 1000);
}

export function stopBypassSummary(): void {
  _started = false;
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_tick) {
    clearInterval(_tick);
    _tick = undefined;
  }
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _raw = undefined;
  _nr = 0;
  _state.clear();
}

export function getBypass(): BypassState | undefined {
  return _state.get("current");
}

// deactivate schaltet den Bypass aus (Banner-Button "Jetzt deaktivieren").
//
// NICHT optimistisch: erst wenn der Server zugestimmt hat, verschwindet die
// Warnung. Bis 21.10.2026 schaltete sie lokal sofort ab -- scheiterte der
// POST (CSRF-Cookie weg, Dienst startet gerade), war die Warnung weg,
// während der Server die Auth-Umgehung weiter offen hatte, und sie kam
// erst mit dem nächsten Poll kommentarlos wieder. Bei einem
// Sicherheitsschalter ist Optimismus fehl am Platz. Wirft bei Fehler.
export async function deactivateBypass(): Promise<void> {
  try {
    await postJSON("/v2-api/settings/local-api-access", { enabled: false });
  } catch (e) {
    // Den echten Stand holen -- vielleicht ist er doch schon aus.
    void refresh();
    throw e;
  }
  // Ein Abruf, der noch unterwegs ist, kennt den Schalter von vorher: er soll
  // das Ausschalten nicht zurücknehmen. Ein Stand, der schon unterwegs ist,
  // bringt dasselbe.
  _nr++;
  if (_raw) setzeRoh({ ..._raw, enabled: false, enabledAt: "" });
}

// beendeBypass: dasselbe für die Knöpfe in Leiste, Kopf und Banner --
// ein Fehlschlag kommt als Meldung mit dem Grund, statt still zu
// verschwinden. true, wenn der Bypass jetzt aus ist.
export async function beendeBypass(): Promise<boolean> {
  try {
    await deactivateBypass();
    return true;
  } catch (e) {
    const grund = e instanceof APIError ? e.detail : e instanceof Error ? e.message : String(e);
    toast.error(t("bypass.aus_fehler", "Anmeldung nicht wieder eingehängt: {grund}").replace("{grund}", grund));
    return false;
  }
}
